import { Controller } from '@hotwired/stimulus'
import SFetch from '@app/utils/sfetch'
import Polling from '@app/utils/polling'
import { SfLog } from '@app/adapters/logger'

export default class extends Controller {
  static targets = ['button']
  static values = {
    actionUrl: String,
    deactivateButton: String,
    successMsg: String,
  }

  declare readonly buttonTarget: HTMLElement
  declare readonly actionUrlValue: string
  declare readonly deactivateButtonValue: string
  declare readonly successMsgValue: string
  private polling?: Polling<() => void>

  connect(): void {
    this.buttonTarget.addEventListener('click', this.handleButtonClick)
  }

  disconnect(): void {
    this.polling?.unsubscribe()
    this.buttonTarget.removeEventListener('click', this.handleButtonClick)
  }

  handleButtonClick = (event: Event): void => {
    const button = event.target as HTMLElement
    event.preventDefault()
    event.stopPropagation()

    this.setButtonWaitingState(button)
    void (async (): Promise<void> => {
      try {
        const response = await this.activatePackage()

        if (!response.success) {
          if (response.message) {
            window.SilverfinFlash.addFlash('error', response.message.toString())
          } else {
            SfLog.error('Activation failed for ' + this.actionUrlValue)
          }
        } else {
          if (
            response.messageBusChannelName &&
            response.messageBusLastId !== undefined
          ) {
            this.polling = new Polling(
              response.messageBusChannelName,
              response.messageBusLastId,
              () => this.packageActivated()
            )
            this.polling.subscribe()
          }
        }
      } catch (error) {
        if (
          typeof error === 'object' &&
          error !== null &&
          'body' in error &&
          typeof error.body !== 'string'
        ) {
          const errorMessage = (error as { body?: { message?: string } }).body
            ?.message as string
          window.SilverfinFlash.addFlash('error', errorMessage.toString(), 3000)
          this.removeButtonWaitingState(button)
        }
      }
    })()
  }

  private setButtonWaitingState(btn: HTMLButtonElement | HTMLElement): void {
    btn.setAttribute('disabled', 'true')
    btn.classList.add(
      'sf-btn-disabled',
      'tw-opacity-50',
      'tw-cursor-not-allowed'
    )
    btn.insertAdjacentHTML(
      'afterbegin',
      `<span id='activation-spinner-wrapper' class='sf-spinner sf-spinner-primary tw-inline-block tw-w-[14px] tw-h-[14px]'></span>`
    )
  }

  private removeButtonWaitingState(btn: HTMLButtonElement | HTMLElement): void {
    btn.removeAttribute('disabled')
    btn.classList.remove(
      'sf-btn-disabled',
      'tw-opacity-50',
      'tw-cursor-not-allowed'
    )
    const spinner = btn.querySelector('#activation-spinner-wrapper')
    spinner?.remove()
  }

  private async activatePackage(): Promise<{
    success: boolean
    message?: string
    messageBusChannelName?: string
    messageBusLastId?: number
  }> {
    const response = await SFetch.raw(this.actionUrlValue, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
    })
    const data = (await response.json()) as {
      success: boolean
      message?: string
      messageBusChannelName?: string
      messageBusLastId?: number
    }
    return data
  }

  private packageActivated(): void {
    window.requestAnimationFrame(() => {
      window.SilverfinFlash.addFlash(
        'success',
        this.successMsgValue.toString(),
        3000
      )
      if (this.deactivateButtonValue !== '') {
        const temp = document.createElement('span')
        temp.innerHTML = this.deactivateButtonValue
        const btn = temp.firstElementChild
        if (btn) {
          this.buttonTarget.replaceWith(btn)
        } else {
          this.buttonTarget.style.display = 'none'
        }
      } else {
        this.buttonTarget.style.display = 'none'
      }
    })

    this.polling?.unsubscribe()
  }
}
